import { ErrorState } from "$components/error-state";
import { Table, type TableColumnDefinition } from "$components/table";
import { useUser } from "$global-providers/user-provider";
import { DifferenceChip } from "$routes/inventory/_lib/difference-chip";
import { formatDate, formatUserName } from "$routes/inventory/_lib/formatting";
import { useGetFacilityLocationName } from "$routes/inventory/_lib/use-facility-locations";
import UserName from "$routes/inventory/_lib/user-name";
import { gql } from "@apollo/client";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useStockTakesListQuery } from "./__generated__/stock-takes-table.gql";

gql`
  query stockTakesList($input: StockTakesInput!, $options: StockTakesOptions) {
    stockTakes(input: $input, options: $options) {
      items {
        id
        locationId
        finalisedAt
        finalisedBy {
          person {
            firstname
            lastname
          }
        }
        aggregateInfo {
          totalLotCount
          reviewedLotCount
          totalNewLotsAdded
          totalQuantityAdded
          totalQuantityRemoved
        }
      }
      totalCount
    }
  }
`;

interface Props {
  locationId?: string | null;
  startDate?: string;
  endDate?: string;
  pageSize?: number;
  onRowClick: (stockTakeId: string) => void;
}

const StockTakesTable = ({
  locationId,
  startDate,
  endDate,
  pageSize = 20,
  onRowClick,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const tableColumns: TableColumnDefinition[] = [
    {
      field: "finalisedAt",
      headerName: formatMessage({
        id: "inventory.stockTakes.table.columnLabel.finalisedAt",
        defaultMessage: "DATE",
      }),
      flex: 1,
    },
    {
      field: "locationName",
      headerName: formatMessage({
        id: "inventory.stockTakes.table.columnLabel.locationName",
        defaultMessage: "LOCATION",
      }),
      flex: 2,
    },
    {
      field: "lotsUpdated",
      headerName: formatMessage({
        id: "inventory.stockTakes.table.columnLabel.lotsUpdated",
        defaultMessage: "LOTS UPDATED",
      }),
      flex: 1,
    },
    {
      field: "numberOfLotsAdded",
      headerName: formatMessage({
        id: "inventory.stockTakes.table.columnLabel.numberOfLotsAdded",
        defaultMessage: "NEW LOTS",
      }),
      flex: 1,
    },
    {
      field: "unitsRemoved",
      headerName: formatMessage({
        id: "inventory.stockTakes.table.columnLabel.unitsRemoved",
        defaultMessage: "UNITS REMOVED",
      }),
      flex: 1,
      alignContents: "right",
      renderCell: (value) => {
        return <DifferenceChip difference={value * -1} />;
      },
    },
    {
      field: "unitsAdded",
      headerName: formatMessage({
        id: "inventory.stockTakes.table.columnLabel.unitsAdded",
        defaultMessage: "UNITS ADDED",
      }),
      flex: 1,
      alignContents: "right",
      renderCell: (value) => {
        return <DifferenceChip difference={value} />;
      },
    },
    {
      field: "finalisedBy",
      headerName: formatMessage({
        id: "inventory.stockTakes.table.columnLabel.finalisedBy",
        defaultMessage: "DONE BY",
      }),
      flex: 1,
      renderCell: (val) => <UserName name={val} />,
    },
  ];

  const { facilityId } = useUser();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useStockTakesListQuery({
    variables: {
      input: {
        facilityId,
        locationId,
        createdAfter: startDate,
        createdBefore: endDate,
        finalised: true,
      },
      options: {
        pageNumber: currentPage,
        pageSize,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const getLocationName = useGetFacilityLocationName();

  if (error) {
    return <ErrorState variant="inline" error={error} />;
  }

  const mappedData = data?.stockTakes?.items.map((item) => {
    return {
      id: item.id,
      finalisedAt: formatDate(item.finalisedAt),
      lotsUpdated: `${item.aggregateInfo.reviewedLotCount ?? 0}/${
        item.aggregateInfo.totalLotCount ?? 0
      }`,
      locationName: getLocationName(item.locationId),
      numberOfLotsAdded: item.aggregateInfo.totalNewLotsAdded ?? 0,
      unitsRemoved: item.aggregateInfo.totalQuantityRemoved ?? 0,
      unitsAdded: item.aggregateInfo.totalQuantityAdded ?? 0,
      finalisedBy: formatUserName(item.finalisedBy),
    };
  });

  return (
    <Table
      loading={loading}
      columns={tableColumns}
      rows={mappedData ?? []}
      noDataMessage={formatMessage({
        id: "inventory.stockTakes.list.noData",
        defaultMessage: "No stocktakes found.",
      })}
      onRowClick={(row) => onRowClick(row.id as string)}
      pagination={{
        page: currentPage,
        pageSize,
        totalRows: data?.stockTakes?.totalCount,
        handlePageChange: (newPageNumber) => {
          setCurrentPage(newPageNumber);
        },
      }}
    />
  );
};

export default StockTakesTable;
